import React, { useCallback, useEffect, useState } from 'react';
import { Card, Badge, Spinner } from 'react-bootstrap';
import { Button } from 'primereact/button';
import axios from 'axios';
import config from '../config';
import { useAuth } from '../context/AuthContext';

const formatDate = (value) => {
    if (!value) return '—';
    return new Date(value).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    });
};

/**
 * Cards awaiting the logged-in user's review (New Taskboard).
 */
const CardReviewQueue = ({ onOpenCard, refreshKey = 0, showEmpty = true, onCountChange }) => {
    const { accessToken, userId } = useAuth();
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);

    const loadQueue = useCallback(async () => {
        if (!accessToken || !userId) return;
        setLoading(true);
        try {
            const response = await axios.get(
                `${config.apiBASEURL}/TaskBoardRoutes/cards/pending-review`,
                {
                    headers: { Authorization: `Bearer ${accessToken}` },
                    params: { user_id: userId },
                }
            );
            const cards = response.data?.cards || [];
            setItems(cards);
            onCountChange?.(cards.length);
        } catch {
            setItems([]);
            onCountChange?.(0);
        } finally {
            setLoading(false);
        }
    }, [accessToken, userId, onCountChange]);

    useEffect(() => {
        loadQueue();
    }, [loadQueue, refreshKey]);

    if (loading) {
        return (
            <div className="card-review-queue mb-3 p-2 border rounded bg-light">
                <Spinner animation="border" size="sm" className="me-2" />
                <span className="small text-muted">Loading review queue...</span>
            </div>
        );
    }

    if (items.length === 0) {
        if (!showEmpty) return null;
        return (
            <div className="card-review-queue mb-3 p-2 border rounded bg-light">
                <span className="small text-muted">No cards awaiting your review.</span>
            </div>
        );
    }

    return (
        <div className="card-review-queue mb-3 p-3 border rounded bg-light">
            <div className="d-flex align-items-center justify-content-between mb-2">
                <h6 className="mb-0">
                    Awaiting my review
                    <Badge bg="warning" text="dark" className="ms-2">
                        {items.length}
                    </Badge>
                </h6>
                <Button
                    icon="pi pi-refresh"
                    text
                    size="small"
                    onClick={loadQueue}
                    title="Refresh"
                />
            </div>
            <div className="d-flex flex-wrap gap-2">
                {items.map((item) => (
                    <Card
                        key={item.card_id}
                        className="shadow-sm border-0"
                        style={{ minWidth: '220px', maxWidth: '280px', cursor: 'pointer' }}
                        onClick={() =>
                            onOpenCard?.({
                                cardId: item.card_id,
                                boardId: item.board_id,
                            })
                        }
                    >
                        <Card.Body className="p-2">
                            <div className="small fw-semibold text-truncate">{item.title}</div>
                            <div className="small text-muted">
                                {item.board_name}
                                {item.brand_name ? ` · ${item.brand_name}` : ''}
                            </div>
                            {item.due_date && (
                                <div className="small text-muted mt-1">
                                    Due: {formatDate(item.due_date)}
                                </div>
                            )}
                            <Badge bg="info" className="mt-1">
                                {item.list_name || 'In review'}
                            </Badge>
                        </Card.Body>
                    </Card>
                ))}
            </div>
        </div>
    );
};

export default CardReviewQueue;
